import { useEffect, useMemo, useRef, useState } from 'react';
import { Building2, Plus, Search, User, X } from 'lucide-react';
import {
  prospectsApi,
  type ProspectDetail,
  type ProspectListItem,
  type ProspectType,
} from '@/services/prospects.api';
import {
  opportunitiesApi,
  type OpportunityDetail,
} from '@/services/opportunities.api';
import { configApi, type ContractType } from '@/services/config.api';
import { apporteursApi, apporteurDisplayName } from '@/services/apporteurs.api';
import { usersApi, type TeamMember } from '@/services/users.api';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Input, Label, Select, Textarea } from '@/components/ui/Input';
import { Banner } from '@/components/ui/Banner';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useHotkey } from '@/hooks/useHotkey';
import { CreateProspectModal } from './CreateProspectModal';
import { displayName } from '@shared/prospect';

interface ProspectOption {
  id: string;
  type: ProspectType;
  displayName: string;
  siret: string | null;
}

interface Props {
  onClose: () => void;
  onCreated: (opportunity: OpportunityDetail) => void;
  /** Prospect pré-sélectionné - utile si la modale est ouverte depuis une fiche prospect. */
  initialProspect?: ProspectOption | null;
}

function toOption(p: ProspectListItem | ProspectDetail): ProspectOption {
  return {
    id: p.id,
    type: p.type,
    displayName: displayName(p),
    siret: p.siret,
  };
}

/**
 * Modale de création d'opportunité - ouverte depuis la palette ⌘K.
 *
 * Champs obligatoires : prospect, type de contrat, commercial, date d'effet.
 * Le prospect est choisi via une combobox avec recherche async (debounce
 * 250 ms sur `prospectsApi.list({ search })`). Un bouton « Nouveau client »
 * ouvre une `CreateProspectModal` par-dessus (z-[60]) ; à la validation, le
 * prospect créé devient automatiquement le prospect sélectionné.
 */
export function CreateOpportunityModal({ onClose, onCreated, initialProspect = null }: Props) {
  const token = useAuthStore((s) => s.accessToken)!;
  const user = useAuthStore((s) => s.user)!;
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const [showCreateProspect, setShowCreateProspect] = useState(false);
  // Focus trap et Escape désactivés quand la modale de création client est
  // au premier plan : elle gère ses propres escape/focus-trap.
  useFocusTrap(!showCreateProspect, dialogRef);
  useHotkey('escape', onClose, { allowInInput: true, enabled: !showCreateProspect });

  // Préférences utilisateur & référentiels
  const [contractTypes, setContractTypes] = useState<ContractType[] | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [cts, members, apporteursList] = await Promise.all([
          configApi.listContractTypes(token),
          usersApi.teamMembers(token),
          apporteursApi.list(token, { is_active: true }),
        ]);
        if (cancelled) return;
        setContractTypes(cts.filter((c) => c.isActive));
        setTeamMembers(members);
        setApporteurOptions(
          apporteursList.map((a) => {
            // person : "Prénom Nom (Employeur)" si employeur saisi.
            // company : juste la raison sociale (l'« employeur » n'a pas de
            // sens pour une structure morale apporteuse).
            const base = apporteurDisplayName(a);
            const label =
              a.type === 'person' && a.company ? `${base} (${a.company})` : base;
            return {
              id: a.id,
              label,
              retrocommissionRate: Number(a.retrocommissionRate),
              retrocommissionRateFees: Number(a.retrocommissionRateFees),
              retrocommissionRateHonoraires: Number(a.retrocommissionRateHonoraires),
            };
          }),
        );
      } catch {
        /* les erreurs remontent au submit via les champs requis */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const [selectedProspect, setSelectedProspect] = useState<ProspectOption | null>(
    initialProspect ?? null,
  );
  const [contractTypeId, setContractTypeId] = useState('');
  const [assignedUserId, setAssignedUserId] = useState(user.id);
  const [effectiveDate, setEffectiveDate] = useState(isoToday());
  const [premiumHt, setPremiumHt] = useState('');
  // Frais & honoraires (€) — décision 2026-04-30. Optionnels, default 0.00
  // côté DB. Champ vide = pas envoyé (sert le DEFAULT serveur).
  const [fees, setFees] = useState('');
  const [honoraires, setHonoraires] = useState('');

  // Filtre les types de contrat selon le type de prospect sélectionné.
  // - Entreprise → types avec `applicableToCompany=true`
  // - Particulier → types avec `applicableToIndividual=true`
  // - Aucun prospect → tous les types actifs
  // Si le type courant n'est plus applicable après changement de prospect,
  // on reset le dropdown (effet ci-dessous).
  const filteredContractTypes = useMemo(() => {
    if (!contractTypes) return [];
    if (!selectedProspect) return contractTypes;
    return contractTypes.filter((c) =>
      selectedProspect.type === 'individual' ? c.applicableToIndividual : c.applicableToCompany,
    );
  }, [contractTypes, selectedProspect]);

  useEffect(() => {
    if (
      contractTypeId &&
      filteredContractTypes.length > 0 &&
      !filteredContractTypes.some((c) => c.id === contractTypeId)
    ) {
      setContractTypeId('');
    }
  }, [contractTypeId, filteredContractTypes]);
  // `premiumTtc` retiré de l'UI 2026-04-27. Le champ DB est préservé
  // (premium_ttc nullable) pour réactivation future sans migration.
  const [commissionRate, setCommissionRate] = useState('');
  const [apporteurId, setApporteurId] = useState<string>('');
  const [apporteurOptions, setApporteurOptions] = useState<
    Array<{
      id: string;
      label: string;
      retrocommissionRate: number;
      retrocommissionRateFees: number;
      retrocommissionRateHonoraires: number;
    }>
  >([]);
  // Refonte 2026-05-23 : le canal d'acquisition `origin` a été déplacé sur
  // le modèle Prospect. Il se renseigne désormais sur la fiche prospect ou
  // à la création du prospect, pas ici.
  const [details, setDetails] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedProspect) {
      setError('Sélectionnez un client.');
      return;
    }
    if (!contractTypeId) {
      setError('Sélectionnez un type de contrat.');
      return;
    }
    if (!assignedUserId) {
      setError('Sélectionnez un commercial.');
      return;
    }
    if (!effectiveDate) {
      setError('Renseignez la date d\u2019effet.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        prospectId: selectedProspect.id,
        contractTypeId,
        assignedUserId,
        effectiveDate,
      };
      const htNum = premiumHt ? Number(premiumHt) : null;
      const rateNum = commissionRate ? Number(commissionRate) : null;
      if (htNum !== null && Number.isFinite(htNum)) body.premiumHt = htNum;
      if (rateNum !== null && Number.isFinite(rateNum)) body.commissionRate = rateNum;
      const feesNum = fees ? Number(fees) : null;
      const honoNum = honoraires ? Number(honoraires) : null;
      if (feesNum !== null && Number.isFinite(feesNum)) body.fees = feesNum;
      if (honoNum !== null && Number.isFinite(honoNum)) body.honoraires = honoNum;
      if (apporteurId) {
        body.apporteurId = apporteurId;
        // Le montant de rétrocom est calculé à la volée côté backend (taux
        // apporteur × commission). Plus de champ saisi côté UI depuis 2026-04-28.
      }
      // Refonte 2026-05-23 : origin déplacé sur le prospect — plus envoyé ici.
      const trimmedDetails = details.trim();
      if (trimmedDetails) body.details = trimmedDetails;
      const created = await opportunitiesApi.create(token, body);
      onCreated(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = useMemo(
    () =>
      !submitting &&
      !!selectedProspect &&
      !!contractTypeId &&
      !!assignedUserId &&
      !!effectiveDate,
    [submitting, selectedProspect, contractTypeId, assignedUserId, effectiveDate],
  );

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-brand/30 px-4 backdrop-blur-sm animate-fade-in"
        role="presentation"
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label="Créer une opportunité"
          onClick={(e) => e.stopPropagation()}
          className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-lg bg-white shadow-modal animate-scale-in"
        >
          <form onSubmit={handleSubmit} className="space-y-5 p-6">
            <div className="flex items-center justify-between border-b border-border-subtle pb-4">
              <div>
                <h2 className="text-lg font-semibold tracking-tight text-brand">
                  Nouvelle opportunité
                </h2>
                <p className="mt-0.5 text-xs text-text-muted">
                  Une opportunité correspond à un type de contrat pour un client donné.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-1 text-text-muted hover:bg-gray-100 hover:text-brand"
                aria-label="Fermer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div>
              <div className="mb-1.5 flex items-baseline justify-between">
                <Label htmlFor="opp-prospect" required>
                  Client
                </Label>
                <button
                  type="button"
                  onClick={() => setShowCreateProspect(true)}
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-text-muted transition-colors hover:text-accent focus-visible:text-accent"
                >
                  <Plus className="h-3 w-3" />
                  Nouveau client
                </button>
              </div>
              <ProspectCombobox
                token={token}
                value={selectedProspect}
                onChange={(p) => setSelectedProspect(p)}
                inputId="opp-prospect"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="opp-contract-type" required>
                  Type de contrat
                </Label>
                <Select
                  id="opp-contract-type"
                  value={contractTypeId}
                  onChange={(e) => setContractTypeId(e.target.value)}
                  disabled={!contractTypes}
                  required
                >
                  <option value="">{contractTypes ? '- Sélectionner -' : 'Chargement…'}</option>
                  {filteredContractTypes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code} - {c.label}
                    </option>
                  ))}
                </Select>
                {selectedProspect && filteredContractTypes.length === 0 && contractTypes && (
                  <p className="mt-1 text-[11px] text-warning-600">
                    Aucun type de contrat n&apos;est configuré pour un{' '}
                    {selectedProspect.type === 'individual' ? 'particulier' : 'entreprise'}.
                    Contactez la direction.
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="opp-assigned" required>
                  Commercial
                </Label>
                <Select
                  id="opp-assigned"
                  value={assignedUserId}
                  onChange={(e) => setAssignedUserId(e.target.value)}
                  disabled={!teamMembers}
                  required
                >
                  {(
                    teamMembers ?? [
                      {
                        id: user.id,
                        firstName: user.firstName,
                        lastName: user.lastName,
                      } as TeamMember,
                    ]
                  ).map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.firstName} {m.lastName}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="opp-effective-date" required>
                Date d&apos;effet
              </Label>
              <Input
                id="opp-effective-date"
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                required
              />
            </div>

            {/* Refonte 2026-05-23 : Origine retirée. Le canal d'acquisition
                est désormais un attribut du prospect (fiche prospect), pas
                de chaque opportunité. Voir CreateProspectModal / ProspectDetailPage. */}

            {/* Bloc financier 2x2 — décision 2026-04-30 Q12 :
                Ligne 1 : Prime HT  · Commission (%)
                Ligne 2 : Frais     · Honoraires
                + ligne récap Total CA si > 0.
                Prime TTC retirée 2026-04-27, champ DB préservé. */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="opp-ht">Prime HT (€)</Label>
                <Input
                  id="opp-ht"
                  type="number"
                  step="0.01"
                  min="0"
                  value={premiumHt}
                  onChange={(e) => setPremiumHt(e.target.value)}
                  placeholder="0,00"
                />
              </div>
              <div>
                <Label htmlFor="opp-rate">Commission (%)</Label>
                <Input
                  id="opp-rate"
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={commissionRate}
                  onChange={(e) => setCommissionRate(e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <Label htmlFor="opp-fees">Frais (€)</Label>
                <Input
                  id="opp-fees"
                  type="number"
                  step="0.01"
                  min="0"
                  value={fees}
                  onChange={(e) => setFees(e.target.value)}
                  placeholder="0,00"
                />
              </div>
              <div>
                <Label htmlFor="opp-honoraires">Honoraires (€)</Label>
                <Input
                  id="opp-honoraires"
                  type="number"
                  step="0.01"
                  min="0"
                  value={honoraires}
                  onChange={(e) => setHonoraires(e.target.value)}
                  placeholder="0,00"
                />
              </div>
            </div>
            {/* Total CA récap (live, 2 décimales fr-FR — décision Q C). */}
            {(() => {
              const ht = Number(premiumHt);
              const rate = Number(commissionRate);
              const com = Number.isFinite(ht) && Number.isFinite(rate) ? (ht * rate) / 100 : 0;
              const f = Number(fees) || 0;
              const h = Number(honoraires) || 0;
              const total = com + f + h;
              if (total <= 0) return null;
              return (
                <div className="-mt-2 rounded-md border border-border-subtle bg-gray-50 px-3 py-2 text-sm">
                  <span className="text-text-muted">Total CA estimé : </span>
                  <span className="font-semibold text-brand">
                    {total.toLocaleString('fr-FR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{' '}
                    €
                  </span>
                </div>
              );
            })()}

            {/* Apporteur d'affaires (optionnel). Si sélectionné, on affiche
                en lecture seule la rétrocommission calculée à la volée
                (= commission × retrocommissionRate / 100) - décision produit
                2026-04-28 : plus de saisie manuelle, le taux vient de la
                fiche apporteur. Liste filtrée actifs uniquement côté API. */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="opp-apporteur">Apporteur</Label>
                <select
                  id="opp-apporteur"
                  value={apporteurId}
                  onChange={(e) => setApporteurId(e.target.value)}
                  className="h-9 w-full rounded-md border border-border-subtle bg-white px-3 text-sm text-brand"
                >
                  <option value="">— Aucun</option>
                  {apporteurOptions.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.label}
                    </option>
                  ))}
                </select>
              </div>
              {apporteurId &&
                (() => {
                  // Breakdown rétrocom 3 composantes (décision 2026-04-30 Q B).
                  const a = apporteurOptions.find((x) => x.id === apporteurId);
                  if (!a) return null;
                  const ht = Number(premiumHt);
                  const rate = Number(commissionRate);
                  const commission =
                    Number.isFinite(ht) && Number.isFinite(rate) ? (ht * rate) / 100 : 0;
                  const f = Number(fees) || 0;
                  const h = Number(honoraires) || 0;
                  const retroCom = (commission * a.retrocommissionRate) / 100;
                  const retroFees = (f * a.retrocommissionRateFees) / 100;
                  const retroHono = (h * a.retrocommissionRateHonoraires) / 100;
                  const retroTotal = retroCom + retroFees + retroHono;
                  const fmt = (v: number) =>
                    v.toLocaleString('fr-FR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    });
                  return (
                    <div>
                      <Label>Rétrocom totale</Label>
                      <div className="rounded-md border border-border-subtle bg-gray-50 px-3 py-2 text-sm">
                        <div className="font-semibold text-brand">{fmt(retroTotal)} €</div>
                        <div className="mt-1 space-y-0.5 text-[11px] text-text-muted">
                          <div>
                            Com : {fmt(retroCom)} € (
                            {a.retrocommissionRate.toString().replace('.', ',')} %)
                          </div>
                          <div>
                            Frais : {fmt(retroFees)} € (
                            {a.retrocommissionRateFees.toString().replace('.', ',')} %)
                          </div>
                          <div>
                            Hono : {fmt(retroHono)} € (
                            {a.retrocommissionRateHonoraires.toString().replace('.', ',')} %)
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
            </div>

            {/* Bloc-notes libre — optionnel à la création, toujours éditable
                depuis la fiche opp. Cap 5000 chars aligné avec le backend
                (Zod) et `prospects.observations`. Compteur live discret. */}
            <div>
              <Label htmlFor="opp-details">Détails</Label>
              <Textarea
                id="opp-details"
                rows={4}
                maxLength={5000}
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Contexte client, points d'attention, historique des échanges…"
              />
              <p className="mt-1 text-[11px] text-text-muted">{details.length}/5000 caractères</p>
            </div>

            {error && <Banner tone="danger">{error}</Banner>}

            <div className="flex justify-end gap-2 border-t border-border-subtle pt-4">
              <Button variant="secondary" onClick={onClose}>
                Annuler
              </Button>
              <Button type="submit" variant="primary" loading={submitting} disabled={!canSubmit}>
                {submitting ? 'Création…' : 'Créer l\u2019opportunité'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {showCreateProspect && (
        <CreateProspectModal
          zClass="z-[60]"
          onClose={() => setShowCreateProspect(false)}
          onCreated={(p: ProspectDetail) => {
            setShowCreateProspect(false);
            setSelectedProspect(toOption(p));
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Combobox prospect - recherche async debounced
// ---------------------------------------------------------------------------

function ProspectCombobox({
  token,
  value,
  onChange,
  inputId,
}: {
  token: string;
  value: ProspectOption | null;
  onChange: (p: ProspectOption | null) => void;
  inputId: string;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ProspectListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Recherche async avec debounce 250ms (min 2 caractères pour éviter les
  // requêtes inutiles - la recherche backend est exacte SIRET ou LIKE sur
  // company_name).
  useEffect(() => {
    if (!open) return;
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const res = await prospectsApi.list(token, { search: term, limit: 10 });
        setResults(res.data);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query, token, open]);

  // Clic en dehors → ferme la liste
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      {value ? (
        <div className="flex items-center justify-between gap-2 rounded-md border border-border-subtle bg-white px-3 py-2 shadow-[inset_0_1px_2px_rgba(27,42,71,0.04)]">
          <div className="flex min-w-0 items-center gap-2">
            {value.type === 'individual' ? (
              <User className="h-4 w-4 shrink-0 text-text-muted" />
            ) : (
              <Building2 className="h-4 w-4 shrink-0 text-text-muted" />
            )}
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-brand">{value.displayName}</div>
              <div className="truncate font-mono text-[11px] text-text-muted">
                {value.siret ?? (value.type === 'individual' ? 'Particulier' : '')}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setQuery('');
              setOpen(true);
            }}
            className="shrink-0 rounded p-1 text-text-muted hover:bg-gray-100 hover:text-brand"
            aria-label="Changer de client"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-muted" />
          <Input
            id={inputId}
            value={query}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            placeholder="Rechercher par nom, SIRET, email ou téléphone…"
            className="pl-8"
            autoComplete="off"
          />
        </div>
      )}

      {open && !value && (
        <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-64 overflow-y-auto rounded-md border border-border-subtle bg-white shadow-popover">
          {query.trim().length < 2 ? (
            <div className="px-3 py-3 text-xs text-text-muted">
              Tapez au moins 2 caractères pour rechercher.
            </div>
          ) : loading ? (
            <div className="px-3 py-3 text-xs text-text-muted">Recherche…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-3 text-xs text-text-muted">
              Aucun client trouvé pour « {query.trim()} ».
            </div>
          ) : (
            <ul>
              {results.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(toOption(p));
                      setOpen(false);
                      setQuery('');
                    }}
                    className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-gray-50"
                  >
                    {p.type === 'individual' ? (
                      <User className="mt-0.5 h-4 w-4 shrink-0 text-text-muted" />
                    ) : (
                      <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-text-muted" />
                    )}
                    <div className="flex flex-col items-start gap-0">
                      <span className="text-sm font-medium text-brand">{displayName(p)}</span>
                      <span className="font-mono text-[11px] text-text-muted">
                        {p.siret ?? p.personalEmail ?? 'Particulier'}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function isoToday(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
